<template>
  <td
    class="date-cell"
    :class="{ editing: isEditing }"
    @click="enterEditMode"
    ref="cellContainer"
  >
    <!-- VIEW MODE -->
    <div
      v-if="!isEditing || !editable"
      class="view-mode"
      :class="{ 'cell-invalid': invalid }"
    >
      <span>{{ Value || placeholder }}</span>
      <i class="far fa-calendar-alt calendar-icon"></i>
    </div>

    <!-- EDIT MODE -->
    <input
      v-else
      ref="textInput"
      type="text"
      class="form-control-clean"
      :value="Value"
      :placeholder="format"
      @input="onInput"
      @keydown="onKeyDown"
      @paste.prevent="onPaste"
      @blur="onBlur"
    />
  </td>
</template>

<script>
import Utils from '@/common/utils.js';
import { EventBus } from '@/event-bus.js';

export default {
  name: 'EditableDateTimeCell',

  props: {
    name: { type: String, required: true },
    value: String, // ISO datetime: YYYY-MM-DDTHH:mm:ss
    editable: { type: Boolean, default: true },
    timeFormat: {
      type: String,
      default: '24h',
      validator: v => ['24h', '12h'].includes(v)
    },
    precision: {
      type: String,
      default: 'mm',
      validator: v => ['mm','mm:ss'].includes(v)
    }
  },

  data() {
    const shortFormat = Utils.getShortDateFormat(); // "DD/MM/YYYY" o "MM/DD/YYYY"
    const dateFormatOrder = shortFormat.startsWith('DD') ? 'DMY' : 'MDY';

    return {
      isEditing: false,
      invalid: false,
      placeholder: shortFormat + ' HH:mm',
      format: shortFormat + ' HH:mm',
      Value: '',
      rawDateDigits: '',
      rawTimeDigits: '',
      period: '',
      dateFormatOrder 
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (!newVal) {
          this.rawDateDigits = '';
          this.rawTimeDigits = '';
          this.period = '';
          this.Value = '';
          return;
        }
        const [datePart, timePart] = newVal.split('T');
        const formattedDate = Utils.formatterDateToString(datePart);
        this.rawDateDigits = formattedDate.replace(/\D/g, '');

        if (timePart) {
          const [h, m, s='00'] = timePart.split(':');
          let hours = parseInt(h,10);
          this.period = '';

          if(this.timeFormat==='12h'){
            this.period = hours>=12?'PM':'AM';
            hours = hours%12 || 12;
          }
          this.rawTimeDigits = hours.toString().padStart(2,'0') + m +
            (this.precision==='mm:ss'?s:'');
        } else {
          this.rawTimeDigits='';
        }
        
        this.Value = this.buildMaskedValue();
      }
    }
  },

  methods:{
    enterEditMode(){
      if(!this.editable) {
        return;
      }
      
      this.isEditing = true;
      this.$nextTick(()=>this.$refs.textInput.focus());
    },

    onBlur(){
      this.$nextTick(()=>{
        if(document.activeElement!==this.$refs.textInput){
          this.isEditing = false;
          this.emitDateTime();
        }
      });
    },

    buildMaskedValue(){
      let result='';
      const d=this.rawDateDigits;

      if(d.length>0) {
        result+=d.substring(0,2);
      }

      if(d.length>2) {
        result+='/'+d.substring(2,4);
      }

      if(d.length>4) {
        result+='/'+d.substring(4,8);
      }

      if(this.rawTimeDigits.length>0) {
        result+=' ';
      }

      const t=this.rawTimeDigits;

      if(t.length>0) {
        result+=t.substring(0,2);
      }

      if(t.length>2) {
        result+=':'+t.substring(2,4);
      }

      if(this.precision==='mm:ss' && t.length>4) {
        result+=':'+t.substring(4,6);
      }

      if(this.timeFormat==='12h' && t.length>=2) {
          result+=this.period?' '+this.period:'';
      }

      return result;
    },

    onInput(e){
      const input = e.target;
      let val = input.value;
      const cursorPos = input.selectionStart;

      const [datePart,...rest] = val.split(' ');
      const timePart = rest.join(' ');

      let dateDigits = datePart.replace(/\D/g,'').substring(0,8);
      this.rawDateDigits = dateDigits;

      let timeDigits = timePart.replace(/\D/g,'');
      const maxTimeLen = this.precision==='mm:ss'?6:4;
      timeDigits = timeDigits.substring(0,maxTimeLen);
      this.rawTimeDigits = timeDigits;

      if(this.timeFormat==='12h'){
        const ampmMatch = timePart.match(/(A|P)M/i);

        if(ampmMatch) {
          this.period = ampmMatch[0].toUpperCase();
        }
      }

      this.Value = this.buildMaskedValue();

      this.$nextTick(()=>{
        let newPos = cursorPos;

        if(cursorPos===2||cursorPos===5) {
          newPos++;
        }

        if(cursorPos===10) {
          newPos++;
        }

        if(cursorPos===13||cursorPos===16) {
          newPos++;
        }

        input.setSelectionRange(newPos,newPos);
      });

      this.emitIfComplete();
    },

    onKeyDown(e){
      const input = e.target;
      const pos = input.selectionStart;
      const allowedKeys=[
        'Backspace',
        'Tab',
        'ArrowLeft',
        'ArrowRight',
        'Delete'
      ];

      if(allowedKeys.includes(e.key)) {
        return;
      }

      if(e.key==='/' && (pos===2||pos===5)){
        e.preventDefault(); 
        input.setSelectionRange(pos+1,pos+1); 
        return;
      }

      if(e.key===':' && (pos===13||pos===16)){
        e.preventDefault(); 
        input.setSelectionRange(pos+1,pos+1); 
        return;
      }

      if(e.key===' ' && pos===10){
        e.preventDefault(); 
        input.setSelectionRange(pos+1,pos+1); 
        return;
      }

      if(this.timeFormat==='12h'){
        if(e.key.toUpperCase()==='A'){ 
          this.period='AM';
          e.preventDefault(); 
          return; 
        }

        if(e.key.toUpperCase()==='P'){ 
          this.period='PM';
          e.preventDefault(); 
          return; 
        }
      }

      if(!/^\d$/.test(e.key)) {
        e.preventDefault();
      }

      if(e.key==='Enter'||e.key==='Tab'){
        e.preventDefault();
        this.emitDateTime();
        this.$emit('next-cell');
      }
    },

    onPaste(e){
      const clipboard=(e.clipboardData||window.clipboardData).getData('text');
      const digits=clipboard.replace(/\D/g,'');
      const maxDateLen=8;
      const maxTimeLen=this.precision==='mm:ss'?6:4;

      this.rawDateDigits=digits.substring(0,maxDateLen);
      this.rawTimeDigits=digits.substring(maxDateLen,maxDateLen+maxTimeLen);

      const ampmMatch=clipboard.match(/(A|P)M/i);

      if(this.timeFormat==='12h' && ampmMatch) {
        this.period=ampmMatch[0].toUpperCase();
      }

      this.Value=this.buildMaskedValue();
      this.emitIfComplete();
    },

    emitIfComplete(){
      const dateComplete=this.rawDateDigits.length===8;
      const timeComplete=this.rawTimeDigits.length=== (this.precision==='mm:ss'?6:4);

      if(dateComplete && timeComplete) {
        this.emitDateTime();
      }
    },

    emitDateTime(){
      if(!this.rawDateDigits||!this.rawTimeDigits){
        this.$emit('input','');
        this.emitChanged('');
        return;
      }

      const day=this.rawDateDigits.substring(0,2);
      const month=this.rawDateDigits.substring(2,4);
      const year=this.rawDateDigits.substring(4,8);
      const isoDate=this.dateOrder==='DMY'?`${year}-${month}-${day}`:
        `${year}-${day}-${month}`;

      let h=parseInt(this.rawTimeDigits.substring(0,2),10);
      const m=this.rawTimeDigits.substring(2,4);
      const s=this.precision==='mm:ss'?this.rawTimeDigits.substring(4,6):'00';

      if(this.timeFormat==='12h'){
        if(this.period==='PM' && h<12) {
          h+=12;
        }

        if(this.period==='AM' && h===12) {
          h=0;
        }
      }
      const hh=h.toString().padStart(2,'0');
      const isoTime=`${hh}:${m}:${s}`;
      const isoDateTime=`${isoDate}T${isoTime}`;

      this.$emit('input',isoDateTime);
      this.emitChanged(isoDateTime);
    },

    emitChanged(value){
      EventBus.$emit('onChanged',{
        Changed:true,
        ComponentName:this.name,
        Value:value
      });
    }
  }
};
</script>

<style scoped>
.date-cell { 
  cursor:pointer; 
  min-width:160px; 
  vertical-align:middle!important; 
  position:relative; 
  padding: 0 !important;
}

.view-mode { 
  display:flex; 
  justify-content:space-between; 
  align-items:center; padding:8px; 
}

.form-control-clean {
  width:100%; height:38px;
  border:2px solid #007bff; 
  padding:0 8px; 
  outline:none; 
  font-family:monospace; 
  background:white; 
}

.calendar-icon { 
  color:#007bff; 
  opacity:0.7; 
}

.date-cell:focus-within { 
  box-shadow: inset 0 0 0 2px #007bff; 
}

.cell-invalid { 
  border:1px solid red; 
}
</style>
